import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import { BrandVerifyBanner } from "../components/BrandVerifyBanner";
import BadgeTier from "../components/BadgeTier";
import StarRating from "../components/StarRating";
import api, { formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Megaphone, Clock, Users, Send, Check, X, Sparkles, Image as ImageIcon, Video as VideoIcon, Trash2 } from "lucide-react";
import SEO from "../components/SEO";
import MatchScoreChip, { MatchScoreDetail } from "../components/MatchScoreChip";
import { DeliverableSpecTable } from "../components/CampaignSpec";
import { ScreeningQuestionsForm, ScreeningAnswers, answersComplete } from "../components/ScreeningQuestions";
import CampaignRoom from "../components/CampaignRoom";
import { ApplicationReceivedDialog } from "../components/ApplicationReceivedDialog";
import { uploadFile } from "../lib/upload";
import { useDraft } from "../lib/useDraft";
import { DraftBar } from "../components/DraftBar";

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.round(ms / 86400000));
}
function toAbsolute(url) {
  if (!url) return url;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}
const PLATFORM_LABELS = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  facebook: "Facebook",
  snapchat: "Snapchat",
  twitter: "X",
  ad_advertising: "Ad advertising",
};
const platformLabel = (p) => PLATFORM_LABELS[p] || p;

export default function CampaignDetail() {
  const { id } = useParams();
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pitch, setPitch] = useState("");
  const [quotedPrice, setQuotedPrice] = useState(0);
  const [applyMode, setApplyMode] = useState("accept_fixed");
  const [bidMessage, setBidMessage] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [answers, setAnswers] = useState([]);
  const [received, setReceived] = useState(false);
  const applyDraft = useDraft(user?.role === "creator" ? `apply:${id}` : null, {
    value: { pitch, quotedPrice, applyMode, bidMessage, shippingAddress, answers },
    isEmpty: (v) => !v || (!v.pitch?.trim() && !v.bidMessage?.trim() && !v.shippingAddress?.trim() && !(v.answers || []).length),
    onRestore: (v) => { setPitch(v.pitch || ""); if (v.quotedPrice) setQuotedPrice(v.quotedPrice); if (v.applyMode) setApplyMode(v.applyMode); setBidMessage(v.bidMessage || ""); setShippingAddress(v.shippingAddress || ""); setAnswers(v.answers || []); },
  });
  const startFreshApply = async () => { await applyDraft.clear(); setPitch(""); setBidMessage(""); setShippingAddress(""); setAnswers([]); };
  const [busy, setBusy] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (!isImage && !isVideo) { toast.error("Choose an image or a short video"); e.target.value = ""; return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Cover must be under 25 MB"); e.target.value = ""; return; }
    setCoverUploading(true);
    try {
      const up = await uploadFile(file, { label: isVideo ? "cover video" : "cover image" });
      const patch = isVideo ? { cover_video_url: up.url } : { cover_url: up.url };
      const { data: updated } = await api.patch(`/campaigns/${id}/cover`, patch);
      setData((d) => ({ ...d, campaign: updated }));
      toast.success(isVideo ? "Cover video updated" : "Cover image updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setCoverUploading(false);
      e.target.value = "";
    }
  };

  const removeCover = async () => {
    if (!window.confirm("Remove the campaign cover?")) return;
    try {
      const { data: updated } = await api.patch(`/campaigns/${id}/cover`, { cover_url: "", cover_video_url: "" });
      setData((d) => ({ ...d, campaign: updated }));
      toast.success("Cover removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/campaigns/${id}`);
      setData(data);
      if (data?.campaign?.budget) setQuotedPrice((v) => v || data.campaign.budget);
      if (data?.campaign?.pricing_mode === "bidding") setApplyMode("bid");
    } catch (e) {
      toast.error("Campaign not found");
    }
  };

  useEffect(() => {
    if (loading) return;
    load();
  }, [id, loading]);

  const apply = async () => {
    if (!user) { navigate("/auth?mode=signup&role=creator"); return; }
    if (user.role !== "creator") { toast.error("Only creators can apply"); return; }
    if (!pitch.trim()) { toast.error("Write a short pitch"); return; }
    if (applyMode === "bid" && !quotedPrice) { toast.error("Enter your bid amount"); return; }
    if (data?.campaign?.needs_shipping && !shippingAddress.trim()) {
      toast.error("This campaign requires a shipping address"); return;
    }
    if (!answersComplete(data?.campaign?.questions, answers)) {
      toast.error("Please answer all of the brand's questions"); return;
    }
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/apply`, {
        pitch,
        quoted_price: Number(quotedPrice) || 0,
        mode: applyMode,
        bid_message: bidMessage,
        shipping_address: shippingAddress,
        answers,
      });
      setReceived(true);
      applyDraft.clear();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const accept = async (aid) => {
    setBusy(true);
    try {
      const { data: h } = await api.post(`/campaigns/${id}/applications/${aid}/accept`);
      toast.success("Application accepted — deal room opened");
      navigate(`/deals/${h.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const rankApplicants = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/rank`);
      toast.success("Ranked by AI match score");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return (
    <div className="min-h-screen grain"><Navbar /><div className="max-w-4xl mx-auto p-10 text-zinc-500 animate-pulse">Loading…</div></div>
  );

  const c = data.campaign;
  const isOwner = user?.id === c.brand_id;
  const my = data.my_application;
  const closed = c.status !== "open";
  const d = daysUntil(c.closes_at);

  return (
    <div className="min-h-screen grain">
      <SEO
        title={`${c.title} · ${c.brand_name} Campaign`}
        description={
          `${c.brand_name} is hiring creators on Nexus. Budget £${c.budget}. ` +
          `${(c.niches || []).join(", ")} · ${(c.platforms || []).join(", ")}. ` +
          `${c.description?.slice(0, 140) || ""}`
        }
        path={`/campaigns/${c.id}`}
        keywords={[
          `${c.brand_name} campaign`,
          ...(c.niches || []).map((n) => `${n} campaign`),
          ...(c.platforms || []).map((p) => `${p} paid collab`),
          "brand deal",
          "paid partnership",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "JobPosting",
          "title": c.title,
          "description": (c.description || "") + (c.deliverables ? `\n\nDeliverables: ${c.deliverables}` : ""),
          "datePosted": c.created_at,
          "validThrough": c.closes_at,
          "employmentType": "CONTRACTOR",
          "hiringOrganization": {
            "@type": "Organization",
            "name": c.brand_name,
          },
          "baseSalary": {
            "@type": "MonetaryAmount",
            "currency": "GBP",
            "value": {
              "@type": "QuantitativeValue",
              "value": c.budget,
            },
          },
          "jobLocationType": "TELECOMMUTE",
          "applicantLocationRequirements": {
            "@type": "Country",
            "name": "Worldwide",
          },
          "occupationalCategory": (c.niches || []).join(", ") || "Content Creator",
        }}
      />
      <Navbar />
      <ApplicationReceivedDialog
        open={received}
        onClose={() => setReceived(false)}
        brandName={c.brand_name}
        brandLogoUrl={c.brand_logo_url}
        creatorName={user?.name}
        creatorAvatarUrl={profile?.avatar_url}
        creatorHandle={profile?.handle ? `@${profile.handle}` : user?.name}
      />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6">
        <BackButton fallback="/campaigns" label="Back to campaigns" />
      </div>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 pb-10 space-y-6">
        {isOwner && c.brand_verified === false && <BrandVerifyBanner compact />}
        {/* Cover hero — image or short autoplaying video */}
        {(c.cover_video_url || c.cover_url || (user && user.id === c.brand_id)) && (
          <div className="relative rounded-3xl overflow-hidden bg-black hairline" data-testid="campaign-cover-hero">
            {c.cover_video_url ? (
              <video
                src={c.cover_video_url}
                className="w-full aspect-[16/9] object-cover"
                autoPlay muted loop playsInline
                data-testid="campaign-cover-video"
              />
            ) : c.cover_url ? (
              <img
                src={c.cover_url}
                alt={c.title}
                className="w-full aspect-[16/9] object-cover"
                data-testid="campaign-cover-image"
              />
            ) : (
              <div className="w-full aspect-[16/9] flex items-center justify-center text-zinc-500 text-sm border-2 border-dashed border-white/10">
                <div className="text-center">
                  <ImageIcon className="h-10 w-10 mx-auto mb-2 text-zinc-600" />
                  <div>No cover yet — add one so this campaign stands out</div>
                </div>
              </div>
            )}
            {user && user.id === c.brand_id && (
              <div className="absolute bottom-3 right-3 flex items-center gap-2" data-testid="campaign-cover-owner-controls">
                <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/20 text-xs text-white hover:bg-black/85 cursor-pointer transition-colors ${coverUploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="campaign-cover-upload">
                  {coverUploading ? "Uploading…" : (<><ImageIcon className="h-3.5 w-3.5" /> {c.cover_url || c.cover_video_url ? "Replace" : "Upload cover"}</>)}
                  <input type="file" onChange={uploadCover} className="hidden" accept="image/*,video/mp4,video/webm,video/quicktime" />
                </label>
                {(c.cover_url || c.cover_video_url) && (
                  <button
                    type="button"
                    onClick={removeCover}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/20 text-xs text-red-300 hover:bg-red-500/20 transition-colors"
                    data-testid="campaign-cover-remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
            )}
            {c.cover_video_url && (
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-black/70 backdrop-blur border border-white/20 text-nexus-pink inline-flex items-center gap-1">
                <VideoIcon className="h-3 w-3" /> Video
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Megaphone className="h-3.5 w-3.5" /> {c.brand_name}
            </div>
            <h1 className="font-display font-bold text-4xl tracking-tighter">{c.title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-nexus-blue flex items-center gap-1"><Clock className="h-3 w-3" />{d}d left</span>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${closed ? "bg-zinc-500/10 text-zinc-300 border-zinc-500/30" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"}`}>
              {c.status}
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <section className="glass rounded-2xl p-6">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Brief</div>
              <p className="whitespace-pre-wrap text-zinc-200 leading-relaxed">{c.description}</p>
            </section>

            {user && <CampaignRoom campaignId={c.id} />}

            {(c.deliverables || c.spec) && (
              <section className="glass rounded-2xl p-6" data-testid="deliverables-section">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Deliverables & instructions</div>
                {c.deliverables && <p className="whitespace-pre-wrap text-zinc-200 leading-relaxed">{c.deliverables}</p>}
                {c.spec && <div className={c.deliverables ? "mt-4 pt-2 border-t border-white/5" : ""}><DeliverableSpecTable spec={c.spec} /></div>}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5" data-testid="content-requirements">
                  {c.edited_content_required && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-nexus-blue/30 bg-nexus-blue/10 text-nexus-blue">Edited content</span>
                  )}
                  {c.raw_footage_required && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-nexus-pink/30 bg-nexus-pink/10 text-nexus-pink">Raw footage included</span>
                  )}
                </div>
              </section>
            )}

            {(c.brief_files?.length > 0 || c.inspiration_links?.length > 0) && (
              <section className="glass rounded-2xl p-6" data-testid="brief-attachments">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Brief & inspiration</div>
                {c.brief_files?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {c.brief_files.map((f, i) => (
                      <a key={i} href={f.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] hairline text-sm text-zinc-200 hover:text-white">
                        📎 <span className="truncate">{f.name || f.url}</span>
                      </a>
                    ))}
                  </div>
                )}
                {c.inspiration_links?.length > 0 && (
                  <div className="space-y-1.5">
                    {c.inspiration_links.map((l, i) => (
                      <a key={i} href={l} target="_blank" rel="noreferrer" className="block text-sm text-nexus-blue hover:underline truncate">↗ {l}</a>
                    ))}
                  </div>
                )}
              </section>
            )}

            {c.needs_shipping && (
              <section className="glass rounded-2xl p-6 border-nexus-blue/20 bg-nexus-blue/5" data-testid="shipping-block">
                <div className="text-xs uppercase tracking-widest text-nexus-blue mb-2">🚚 Product ships to creator</div>
                {c.shipping_note && <p className="text-sm text-zinc-200 whitespace-pre-wrap">{c.shipping_note}</p>}
              </section>
            )}

            {isOwner && c.targets && Object.values(c.targets).some((v) => v > 0) && (
              <section className="glass rounded-2xl p-6" data-testid="motivation-tracker">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">🎯 Motivation tracker</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    { k: "ads_per_week", label: "Ads/week", actual: c.delivered_ads || 0 },
                    { k: "views", label: "Views" },
                    { k: "engagement", label: "Engagement" },
                    { k: "conversions", label: "Conversions" },
                    { k: "sales", label: "Sales" },
                  ].map((t) => {
                    const target = c.targets[t.k] || 0;
                    if (!target) return null;
                    const actual = t.actual ?? 0;
                    const pct = Math.min(100, (actual / Math.max(target, 1)) * 100);
                    return (
                      <div key={t.k} className="p-3 rounded-lg bg-white/[0.02] hairline">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{t.label}</div>
                        <div className="font-display font-bold text-lg tabular-nums">{actual}<span className="text-zinc-500 text-xs">/{target}</span></div>
                        <div className="h-1 rounded-full bg-white/5 overflow-hidden mt-1.5">
                          <div className="h-full nexus-gradient" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-zinc-500 mt-3">Ship live content and Nexus updates these automatically.</p>
              </section>
            )}

            {isOwner && (
              <section className="glass rounded-2xl p-6">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Applications ({data.applications.length})</div>
                  {data.applications.length > 0 && (
                    <button
                      onClick={rankApplicants}
                      disabled={busy}
                      data-testid="rank-applicants-btn"
                      className="text-[11px] uppercase tracking-widest font-bold px-3 h-8 rounded-full nexus-gradient text-white hover:opacity-90 inline-flex items-center gap-1.5"
                    >
                      <Sparkles className="h-3 w-3" /> Rank by AI match
                    </button>
                  )}
                </div>
                {data.applications.length === 0 ? (
                  <p className="text-sm text-zinc-500">No applications yet. Give it a moment.</p>
                ) : (
                  <div className="space-y-3" data-testid="applications-list">
                    {[...data.applications]
                      .sort((a, b) => (b.ai_match?.score || 0) - (a.ai_match?.score || 0))
                      .map((a) => (
                      <div key={a.id} className="p-4 rounded-xl bg-white/[0.02] hairline" data-testid={`application-row-${a.id}`}>
                        <div className="flex items-start gap-3">
                          <Link to={`/creators/${a.creator?.user_id}`} className="h-11 w-11 rounded-full overflow-hidden hairline bg-black/40 shrink-0">
                            {a.creator?.avatar_url && (
                              <img src={a.creator.avatar_url.startsWith("http") ? a.creator.avatar_url : `${window.location.origin}${a.creator.avatar_url}`} alt="" className="w-full h-full object-cover" />
                            )}
                          </Link>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link to={`/creators/${a.creator?.user_id}`} className="font-semibold hover:underline">
                                {a.creator?.name || a.creator_name}
                              </Link>
                              <BadgeTier tier={a.creator?.badge_tier || "bronze"} size="sm" />
                              <StarRating value={a.creator?.star_rating || 0} size="sm" />
                              <MatchScoreChip match={a.ai_match} testId={`match-score-${a.id}`} />
                            </div>
                            <p className="text-sm text-zinc-300 whitespace-pre-wrap mt-1.5">{a.pitch}</p>
                            <ScreeningAnswers answers={a.answers} compact />
                            <MatchScoreDetail match={a.ai_match} />
                          </div>
                          <div className="text-right">
                            <div className="font-display font-bold text-lg">£{a.quoted_price}</div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500">quoted</div>
                          </div>
                        </div>
                        {a.status === "pending" ? (
                          <div className="mt-4 flex gap-2 justify-end">
                            <Button
                              onClick={() => accept(a.id)}
                              disabled={busy || closed}
                              className="nexus-gradient text-white rounded-full border-0 h-9 text-xs font-semibold hover:opacity-90"
                              data-testid={`accept-application-${a.id}`}
                            >
                              <Check className="h-3.5 w-3.5 mr-1.5" /> Accept & open deal
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500 text-right">
                            {a.status === "accepted" ? "✓ Accepted" : a.status === "rejected" ? "✗ Rejected" : a.status}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>

          <aside className="space-y-6">
            <div className="glass rounded-2xl p-6 space-y-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {c.pricing_mode === "bidding" ? "Budget ceiling" : "Fixed fee"}
              </div>
              <div className="font-display font-black text-5xl nexus-gradient-text leading-none">£{c.budget}</div>
              <div className="text-[10px] uppercase tracking-widest">
                {c.pricing_mode === "bidding" ? (
                  <span className="px-2 py-0.5 rounded-full border border-nexus-purple/30 bg-nexus-purple/10 text-nexus-purple">Open to bids</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">Fixed fee</span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {(c.niches || []).map((n) => (
                  <span key={n} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.02] text-zinc-300">{n}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(c.platforms || []).map((p) => (
                  <span key={p} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${p === "ad_advertising" ? "border-nexus-pink/40 bg-nexus-pink/10 text-nexus-pink" : "border-white/10 bg-white/[0.02] text-zinc-300"}`}>
                    {p === "ad_advertising" && "🚀 "}
                    {platformLabel(p)}
                  </span>
                ))}
              </div>
              <div className="text-sm text-zinc-400 flex items-center gap-2 pt-2 border-t border-white/5">
                <Users className="h-4 w-4" /> {c.applications_count || 0} applications
              </div>
            </div>

            {!isOwner && user?.role !== "brand" && !closed && !my && (
              <div className="glass rounded-2xl p-6 space-y-4" data-testid="apply-panel">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Respond</div>
                {c.pricing_mode === "bidding" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => { setApplyMode("accept_fixed"); setQuotedPrice(c.budget); }}
                      data-testid="mode-accept-btn"
                      className={`p-3 rounded-xl text-left border text-xs transition-colors ${applyMode === "accept_fixed" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"}`}
                    >
                      <div className="font-semibold mb-0.5">Accept £{c.budget}</div>
                      <div className="text-[10px] opacity-70">Take the ceiling and start</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setApplyMode("bid")}
                      data-testid="mode-bid-btn"
                      className={`p-3 rounded-xl text-left border text-xs transition-colors ${applyMode === "bid" ? "border-nexus-purple/40 bg-nexus-purple/10 text-white" : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25"}`}
                    >
                      <div className="font-semibold mb-0.5">Submit your bid</div>
                      <div className="text-[10px] opacity-70">Propose your rate</div>
                    </button>
                  </div>
                ) : (
                  <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-sm text-emerald-200">
                    Fixed fee — <b>£{c.budget}</b>. Submit your pitch to accept.
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Your pitch</Label>
                  <Textarea data-testid="apply-pitch-input" value={pitch} onChange={(e) => setPitch(e.target.value)} rows={4} placeholder="Why you? Any relevant work?" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
                </div>
                {applyMode === "bid" && (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Your bid (£)</Label>
                      <Input type="number" data-testid="apply-price-input" value={quotedPrice} onChange={(e) => setQuotedPrice(e.target.value)} className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Why this rate?</Label>
                      <Textarea data-testid="bid-message-input" value={bidMessage} onChange={(e) => setBidMessage(e.target.value)} rows={2} placeholder="Justify your bid — deliverables, timeline, extras…" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
                    </div>
                  </>
                )}
                <DraftBar draft={applyDraft} onStartFresh={startFreshApply} />
                <ScreeningQuestionsForm questions={c.questions} answers={answers} onChange={setAnswers} />
                {c.needs_shipping && (
                  <div data-testid="shipping-input-block">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">🚚 Shipping address</Label>
                    <Textarea data-testid="shipping-address-input" value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} rows={3} placeholder="Full delivery address for product samples" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
                  </div>
                )}
                <Button onClick={apply} disabled={busy} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 w-full" data-testid="apply-submit-btn">
                  <Send className="h-4 w-4 mr-2" /> {applyMode === "bid" ? `Submit bid · £${quotedPrice || 0}` : `Accept · £${c.budget}`}
                </Button>
              </div>
            )}

            {my && (
              <div className="glass rounded-2xl p-6 space-y-3" data-testid="my-application-panel">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Your application</div>
                <div className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded-full inline-flex items-center border ${
                  my.status === "accepted" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                  my.status === "rejected" ? "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30" :
                  "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30"
                }`}>
                  {my.status === "accepted" ? <><Check className="h-3 w-3 mr-1" /> accepted</> :
                   my.status === "rejected" ? <><X className="h-3 w-3 mr-1" /> rejected</> :
                   "pending"}
                </div>
                <div className="text-sm">
                  <span className="text-zinc-500">Your quote · </span>
                  <span className="font-semibold">£{my.quoted_price}</span>
                </div>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap">{my.pitch}</p>
                <ScreeningAnswers answers={my.answers} />
                {my.hire_id && (
                  <Link to={`/deals/${my.hire_id}`}>
                    <Button variant="outline" className="w-full bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full">
                      Open deal room
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
